// stores/site.ts — состояние сайта: загрузка (GET /site), dirty-мутации в памяти,
// save (PUT /site с compile-time factRef-проверкой), publish (загоняет в pipeline).
// Это единый источник правды для всех табов редактора.
import { defineStore } from 'pinia';
import { computed, ref, shallowRef, watch } from 'vue';
import type { Site, Page, Block, Post, FormDef, ProductFull, CategoryFull, Integration } from '@mcpsite/core';
import { api, ApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { useToast } from '@/stores/toast';

export const useSiteStore = defineStore('site', () => {
  // shallowRef — Site большой, реактивность на верхнем уровне через замены значений полей
  const site = shallowRef<Site | null>(null);
  /** Проект (x-project slug), для которого загружен site.value. Защита save() от записи
   *  ЧУЖОГО сайта: если projectSlug сменился после load (гонка переключения), данные
   *  устарели и сохранять их в новый проект нельзя — иначе перезапишем чужой site_state. */
  const loadedProjectSlug = ref<string | null>(null);
  const loading = ref(false);
  const saving = ref(false);
  const lastSavedAt = ref<Date | null>(null);
  /** Время последней успешной публикации (CP0.2/CP1.5: статус «опубликован HH:MM»). */
  const lastPublishedAt = ref<Date | null>(null);
  /** Счётчик локальных правок — для dirty-индикатора в шапке (⌘S). */
  const dirtyCount = ref(0);
  const error = ref<string | null>(null);

  const isDirty = computed(() => dirtyCount.value > 0);

  // B10: sequence-guard — при быстрой смене проектов отклик «старого» load() не должен
  // перезаписывать site «нового» проекта (гонка: старый запрос пришёл позже).
  let loadSeq = 0;
  async function load(): Promise<void> {
    const auth = useAuthStore();
    // guard: без активного проекта /api/site отдаст 404 (мусорный запрос при переключении)
    if (!auth.projectSlug) return;
    const my = ++loadSeq;
    const slug = auth.projectSlug; // проект, для которого грузим (заголовок x-project)
    loading.value = true; error.value = null;
    try {
      const res = await api.get<{ site: Site; public?: boolean }>('/api/site');
      if (my !== loadSeq) return; // уже стартовал новый load — отбрасываем устаревший отклик
      site.value = res.site;
      loadedProjectSlug.value = slug;
      dirtyCount.value = 0;
    } catch (e) {
      if (my === loadSeq) error.value = (e as Error).message;
    } finally {
      if (my === loadSeq) loading.value = false;
    }
  }

  /** Отметить правку (вызывают мутации страниц/блоков). */
  function touch(): void { dirtyCount.value++; }

  /** Полный сброс (смена проекта): старый site/dirty не «утекают» в новый проект.
   *  Критично: без этого «Сохранить» сразу после переключения пишет ЧУЖОЙ сайт в новый проект. */
  function reset(): void {
    site.value = null;
    loadedProjectSlug.value = null;
    dirtyCount.value = 0;
    lastSavedAt.value = null;
    lastPublishedAt.value = null;
  }
  function resetDirty(): void { dirtyCount.value = 0; }

  // ── Мутации (in-place, с touch()) ──────────────────────────────────────
  function updatePage(slug: string, mut: (p: Page) => void): void {
    const s = site.value; if (!s) return;
    const p = s.pages.find((x) => x.slug === slug);
    if (!p) return;
    mut(p);
    // вызываем реактивность через присваивание новой ссылки на pages
    s.pages = [...s.pages];
    site.value = { ...s };
    touch();
  }
  function addPage(page: Page): void {
    const s = site.value; if (!s) return;
    s.pages = [...s.pages, page];
    site.value = { ...s };
    touch();
  }
  function removePage(slug: string): void {
    const s = site.value; if (!s) return;
    s.pages = s.pages.filter((p) => p.slug !== slug);
    site.value = { ...s };
    touch();
  }
  function setPages(pages: Page[]): void {
    const s = site.value; if (!s) return;
    s.pages = pages;
    site.value = { ...s };
    touch();
  }

  // Аналогично для posts/header/footer/popups/integrations/org/theme
  function mutate<T>(selector: (s: Site) => T, apply: (target: T, s: Site) => void): void {
    const s = site.value; if (!s) return;
    apply(selector(s), s);
    site.value = { ...s };
    touch();
  }

  // ── B2: точечная синхронизация после CRUD (без touch, без load) ─────────────
  // PUT /site — ПОЛНАЯ замена site_state: устаревший store при «Сохранить» откатывает
  // сущности, созданные через CRUD-роуты. Синхронизируем store сразу после успешного
  // CRUD — зеркало серверного синка (routes/products.ts), но БЕЗ touch(): локальные
  // правки страниц не должны теряться, и БЕЗ load(): он сбрасывает dirtyCount.
  function syncProduct(product: ProductFull, removed = false): void {
    const s = site.value; if (!s) return;
    if (removed) s.products = s.products.filter((p) => p.id !== product.id);
    else {
      const i = s.products.findIndex((p) => p.id === product.id);
      if (i >= 0) s.products[i] = product;
      else s.products = [...s.products, product];
    }
    site.value = { ...s };
  }
  /** B2: страница, созданная сервером автоматически (required-pages сеты). */
  function syncPage(page: Page, removed = false): void {
    const s = site.value; if (!s) return;
    if (removed) s.pages = s.pages.filter((p) => p.slug !== page.slug);
    else {
      const i = s.pages.findIndex((p) => p.slug === page.slug);
      if (i >= 0) s.pages[i] = page;
      else s.pages = [...s.pages, page];
    }
    site.value = { ...s };
  }
  function syncPost(post: Post, removed = false): void {
    const s = site.value; if (!s) return;
    if (removed) s.posts = s.posts.filter((p) => p.id !== post.id);
    else {
      const i = s.posts.findIndex((p) => p.id === post.id);
      if (i >= 0) s.posts[i] = post;
      else s.posts = [...s.posts, post];
    }
    site.value = { ...s };
  }

  // ── Save / Publish ─────────────────────────────────────────────────────
  async function save(): Promise<boolean> {
    const s = site.value;
    if (!s || saving.value) return false;
    // Защита от записи ЧУЖОГО сайта: данные должны принадлежать текущему проекту.
    // Если projectSlug сменился после load (гонка переключения) — site.value устарел,
    // а PUT /site полностью перезапишет site_state нового проекта. Блокируем и
    // перезагружаем актуальный сайт вместо того, чтобы уничтожить чужие данные.
    const cur = useAuthStore().projectSlug;
    if (loadedProjectSlug.value !== cur) {
      useToast().error('Данные не сохранены: проект переключён — перезагружаем');
      reset();
      if (cur) void load();
      return false;
    }
    saving.value = true;
    try {
      const result = await api.put<{ ok: true; brandKitRevision: number; autoCreatedPages?: Page[] }>('/api/site', { ...s, expectedRevision: s.brandKit.revision });
      site.value = { ...s, brandKit: { ...s.brandKit, revision: result.brandKitRevision } };
      // required-pages: автораскрытые страницы (сеты) — синхронизируем в store без touch
      for (const p of result.autoCreatedPages ?? []) syncPage(p);
      dirtyCount.value = 0;
      lastSavedAt.value = new Date();
      useToast().success('Сохранено');
      if (result.autoCreatedPages?.length) {
        useToast().info('Автоматически созданы: ' + result.autoCreatedPages.map((p) => p.slug).join(', '));
      }
      return true;
    } catch (e) {
      if (e instanceof ApiError) {
        const issues = (e.body as { issues?: string[] }).issues ?? [];
        if (e.status === 400 && issues.length) {
          // invalid_site: сервер отклонил данные — показываем, что именно невалидно
          // (раньше был просто «Не удалось сохранить» — непонятно, что чинить).
          useToast().error('Ошибка данных: ' + issues.slice(0, 2).join(' …'));
          console.error('PUT /site 400 invalid_site:', issues);
        } else if (e.status === 409) {
          useToast().error('Бренд-кит уже изменён в другой сессии. Перезагрузите проект.');
        } else if (e.status === 422) {
          useToast().error('Незакрытые факты: ' + issues.slice(0, 2).join(' …'));
        } else if (e.status === 403) {
          useToast().error('Нет прав на сохранение');
        } else {
          useToast().error(`Не удалось сохранить (${e.status})`);
        }
      } else {
        useToast().error('Не удалось сохранить');
      }
      return false;
    } finally {
      saving.value = false;
    }
  }

  async function publish(): Promise<boolean> {
    // publish = сохранить + триггер пересборки (сервер сам шлёт scheduleRebuild на PUT /site)
    const ok = await save();
    if (ok) {
      lastPublishedAt.value = new Date();
      useToast().success('Опубликовано — пересборка пошла');
    }
    return ok;
  }

  // ── CP5.4: автосохранение (debounce ~2с после правки) ──────────────────
  let autosaveTimer: ReturnType<typeof setTimeout> | null = null;
  let autosaveActive = false;
  function scheduleAutosave(): void {
    if (!autosaveActive) return;
    if (autosaveTimer) clearTimeout(autosaveTimer);
    autosaveTimer = setTimeout(() => { void save(); }, 2000);
  }
  function startAutosave(): void {
    autosaveActive = true;
    // watch dirtyCount: при каждой правке пересчитываем таймер
    watch(dirtyCount, () => { if (autosaveActive) scheduleAutosave(); });
  }
  function stopAutosave(): void {
    autosaveActive = false;
    if (autosaveTimer) { clearTimeout(autosaveTimer); autosaveTimer = null; }
  }

  // ── Селекторы для табов ─────────────────────────────────────────────────
  const products = computed<ProductFull[]>(() => []);   // продукты живут в отдельной таблице — подгружаются CatalogTab
  const categories = computed<CategoryFull[]>(() => []);
  const integrations = computed<Integration[]>(() => site.value?.integrations ?? []);

  return {
    site, loading, saving, lastSavedAt, lastPublishedAt, dirtyCount, isDirty, error, loadedProjectSlug,
    load, save, publish, touch, resetDirty, reset, startAutosave, stopAutosave,
    updatePage, addPage, removePage, setPages, mutate, syncProduct, syncPost, syncPage,
    products, categories, integrations,
  };
});

export type { Page, Block, Post, FormDef };
